import React, {useMemo, useState} from 'react';
import i18n from '../../../utils/i18n';
import PropTypes from 'prop-types';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import Button from '@adsk/bim360-matrix-react-button';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Loader from '@adsk/bim360-matrix-react-loader';
import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import ModalConfirmation from '@adsk/bim360-matrix-react-modal-confirmation';
import PermissionSelector from '@adsk/bim360-matrix-react-permission-selector';
import Text from '@adsk/bim360-matrix-react-text';
import {
  AdminPermissionLabels,
  AdminPermissionsArray,
  NewAdminPermissionsArray,
  PermissionsLevelsToIds,
  PermissionsResourceTypes
} from '../../../constants/issues-admin';
import cx from 'classnames';

const IssuesPermissionsTable = ({
  isPermissionDetailModalOpen,
  onLearnMore,
  className,
  highlightPermissions,
  container,
  permissions,
  isFetchingPermissions,
  isDeletingEntry,
  updateIssuesPermissions,
  deleteIssuesPermissionsEntry,
  showNotificationAction
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);
  const adminPermissionsArray = useMemo(
    () =>
      NewAdminPermissionsArray.map(permission =>
        Object.assign({}, permission, {
          label: i18n.t(`issuesAdmin.permissions.permissionsType.${permission.label}`)
        })
      ),
    []
  );
  const oldAdminPermissionsArray = useMemo(
    () =>
      AdminPermissionsArray.map(permission =>
        Object.assign({}, permission, {
          label: i18n.t(`issuesAdmin.permissions.permissionsType.${permission.label}`)
        })
      ),
    []
  );

  return (
    <div className={cx('IssuesPermissionsTable', className)}>
      {isFetchingPermissions ? (
        <Loader />
      ) : permissions.length === 0 ? (
        <EmptyState
          icon={EmptyState.Icons.NOT_FOUND}
          className="IssuesPermissionsTable__empty-state-no-rows"
          text={i18n.t('issuesAdmin.permissions.emptyStateMessage')}
        />
      ) : (
        <MatrixTable
          autoGrow
          className="IssuesPermissionsTable__Table"
          data={permissions}
          loading={isFetchingPermissions}
          highlightedRowKeys={highlightPermissions.map(({subjectId}) => subjectId)}
          rowKey="subjectId"
        >
          {[
            <MatrixTable.Column
              title={i18n.t('issuesAdmin.permissions.tableHeaderName')}
              key="name-column"
              dataKey="name"
              className="IssuesPermissionsTable__name-column"
              headerClassName="IssuesPermissionsTable__name"
              width={100}
              minWidth={100}
              flexGrow={0.8}
              cellRenderer={({cellData, rowData}) =>
                rowData.isProjectAdmin ? (
                  <span className="IssuesPermissionsTable__name">
                    {cellData}
                    <span
                      className="IssuesPermissionsTable__name--admin"
                      data-testid="IssuesPermissionsTable__name--admin"
                    >
                      {i18n.t('issuesAdmin.permissions.rowAdmin')}
                    </span>
                  </span>
                ) : (
                  cellData
                )}
            />,
            <MatrixTable.Column
              title={i18n.t('issuesAdmin.permissions.tableHeaderType')}
              key="type-column"
              dataKey="subjectType"
              className="IssuesPermissionsTable__type-column"
              width={100}
              cellRenderer={({cellData}) => i18n.t(`assignment.${cellData.toLowerCase()}`)}
            />,
            <MatrixTable.Column
              title={i18n.t('issuesAdmin.permissions.tableHeaderIssues')}
              key="issues-column"
              dataKey={PermissionsResourceTypes.ISSUES}
              className="IssuesPermissionsTable__issues-column"
              width={195}
              headerClassName="IssuesPermissionsTable__issues-column-header"
              cellRenderer={({cellData: permission, rowData}) => {
                const permissionValue = rowData.isProjectAdmin
                  ? NewAdminPermissionsArray.find(option => option.label === AdminPermissionLabels.FULL_CONTROL).id
                  : permission.value;

                return (
                  <PermissionSelector
                    readOnly={rowData.isProjectAdmin}
                    permissions={permissionValue === PermissionsLevelsToIds.none ? oldAdminPermissionsArray : adminPermissionsArray}
                    selectedPermissionId={permissionValue}
                    onClick={newValue => {
                      updateIssuesPermissions(permission.id, newValue);
                    }}
                    onLearnMore={onLearnMore}
                    canToggle={!isPermissionDetailModalOpen}
                    container={container}
                  />
                );
              }}
            />,
            <MatrixTable.Column
              key="remove-column"
              width={80}
              className="IssuesPermissionsTable__remove-column"
              cellRenderer={({rowData: permission}) =>
                permission.isProjectAdmin ? null : (
                  <Button
                    className="IssuesPermissionsTable__remove-button"
                    data-testid="IssuesPermissionsTable__remove-button"
                    styleType={Button.StyleType.LINK_SECONDARY}
                    onClick={() => {
                      setShowDeleteConfirmation(true);
                      setPermissionToDelete(permission);
                    }}
                  >
                    {i18n.t('issuesAdmin.permissions.rowRemove')}
                  </Button>
                )}
            />
          ]}
        </MatrixTable>
      )}
      {showDeleteConfirmation ? (
        <ModalConfirmation
          onExited={() => {
            setShowDeleteConfirmation(false);
            setPermissionToDelete(null);
          }}
          onConfirm={() => {
            deleteIssuesPermissionsEntry(
              permissionToDelete[PermissionsResourceTypes.ISSUES].id,
              () => {
                showNotificationAction({
                  type: NotificationTypes.TOAST,
                  level: NotificationLevels.SUCCESS,
                  message: (
                    <Text hasHTML>
                      {i18n.t('issuesAdmin.permissions.notifications.deleteEntrySuccess', {
                        name: permissionToDelete.name
                      })}
                    </Text>
                  )
                });
                setShowDeleteConfirmation(false);
              },
              () => {
                showNotificationAction({
                  type: NotificationTypes.SNACKBAR,
                  level: NotificationLevels.ERROR,
                  message: (
                    <Text hasHTML>
                      {i18n.t('issuesAdmin.permissions.notifications.deleteEntryFailed', {
                        name: permissionToDelete.name
                      })}
                    </Text>
                  )
                });
                setShowDeleteConfirmation(false);
              }
            );
          }}
          title={i18n.t('issuesAdmin.permissions.deleteModal.title')}
          message={<div>{i18n.t('issuesAdmin.permissions.deleteModal.message')}</div>}
          confirmationButtonLabel={i18n.t('issuesAdmin.permissions.deleteModal.remove')}
          confirmationButtonLabelInProgress={i18n.t('issuesAdmin.permissions.deleteModal.removing')}
          isOnConfirmInProgress={isDeletingEntry}
          cancelButtonLabel={i18n.t('issuesAdmin.permissions.deleteModal.cancel')}
          container={container}
        />
      ) : null}
    </div>
  );
};

IssuesPermissionsTable.propTypes = {
  isPermissionDetailModalOpen: PropTypes.bool,
  onLearnMore: PropTypes.func,
  className: PropTypes.string,

  highlightPermissions: PropTypes.arrayOf(
    PropTypes.shape({
      subjectId: PropTypes.string.isRequired,
      subjectType: PropTypes.string.isRequired
    })
  ).isRequired,
  container: PropTypes.object,

  // From store
  permissions: PropTypes.array,
  isFetchingPermissions: PropTypes.bool.isRequired,
  isDeletingEntry: PropTypes.bool,

  // Dispatch
  updateIssuesPermissions: PropTypes.func.isRequired,
  deleteIssuesPermissionsEntry: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired
};

IssuesPermissionsTable.displayName = 'IssuesPermissionsTable';

export default IssuesPermissionsTable;
